import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  TextField,
  IconButton,
  InputAdornment,
  Box,
  Typography,
  Link,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
  Paper,
  Collapse,
  Tooltip,
} from '@mui/material';
import { UserPlus, Copy, ExternalLink, Info } from 'lucide-mui';

import ActionDialog from '../../globalModals/ActionDialog';
import type { GlobalDialogProps } from '../../../types';
import { RootState } from '../../../reducers';
import { getJoinUrlThunk } from '../teamSlice';
import { getJoinUrl } from '../../../lib/routingUtils';
import { usePlan } from '../../plan/hooks/plan';

export const InviteLinkDialog = ({ onClose }: GlobalDialogProps) => {
  const dispatch = useDispatch();
  const { team } = useSelector((state: RootState) => state);
  const { teamId, joinCode } = team?.selectedTeam || {};
  const [inviteLink, setInviteLink] = useState('');
  const { isPlan } = usePlan();
  const isPaidUser = isPlan(['GROWTH', 'BUSINESS', 'ENTERPRISE']);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [showLink, setShowLink] = useState(!isPaidUser);

  useEffect(() => {
    if (!joinCode && teamId) {
      dispatch(getJoinUrlThunk(teamId));
    }
  }, [dispatch, joinCode, teamId]);

  useEffect(() => {
    if (joinCode) {
      setInviteLink(getJoinUrl(joinCode));
    }
  }, [joinCode]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setSnackbarOpen(true);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleAcknowledgement = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setHasAcknowledged(event.target.checked);
    setShowLink(event.target.checked);
  };

  return (
    <ActionDialog
      title="Invite Team Members"
      open
      onClose={handleClose}
      actions={[
        {
          key: 'Close',
          label: 'Done',
          onClick: handleClose,
        },
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          pt: 1,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <UserPlus
            sx={{
              fontSize: 40,
              color: 'primary.main',
            }}
          />
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ px: 1 }}
        >
          Add team members by sharing your invitation link below
        </Typography>

        {isPaidUser && (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              bgcolor: 'background.default',
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              mx: 0.5,
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Info
                color="primary"
                sx={{
                  fontSize: 20,
                  mt: 0.25,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Each new team member will be added to your current plan. Your
                monthly billing will update to reflect team size changes.
              </Typography>
            </Box>
          </Paper>
        )}

        {isPaidUser && (
          <FormControlLabel
            sx={{
              mx: 0.5,
              width: 'auto',
              justifyContent: 'center',
              my: 0,
            }}
            control={
              <Checkbox
                checked={hasAcknowledged}
                onChange={handleAcknowledgement}
                color="primary"
                sx={{ mr: 1 }}
              />
            }
            label={
              <Typography variant="body2">
                I understand my billing will adjust with team size
              </Typography>
            }
          />
        )}

        <Collapse in={!isPaidUser || showLink} timeout={300}>
          <Box sx={{ mx: 0.5 }}>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                bgcolor: 'background.default',
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <TextField
                value={inviteLink}
                variant="outlined"
                fullWidth
                placeholder="Your team invitation link"
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy invitation link">
                        <IconButton
                          onClick={handleCopyLink}
                          color="primary"
                          size="small"
                        >
                          <Copy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                  readOnly: true,
                }}
              />
            </Paper>
          </Box>
        </Collapse>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mt: 1,
            mb: 0,
          }}
        >
          <Link
            href="https://forms.gle/hu9oPT7nGYJAiBud6"
            target="_blank"
            rel="noopener"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: (theme) => theme.palette.primary.main,
              textDecoration: 'none',
              py: 0.5,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Enquire about a team plan
            <ExternalLink sx={{ fontSize: '1rem', ml: 0.5 }} />
          </Link>
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: '100%' }}
        >
          Invitation link copied!
        </Alert>
      </Snackbar>
    </ActionDialog>
  );
};

export default InviteLinkDialog;
